import { Component, Directive, ChangeDetectionStrategy } from '@angular/core';
import { ConsultantService } from '../services/consultant.service';
import { FormControl } from '@angular/forms';
import { Consultant } from '../models/consultant.model';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { combineLatest, startWith } from 'rxjs';
import { OrderService } from '../services/order.service';
import { AuthService } from '../services/auth.service';
import { FeeBasisByConsultantDto, FeeBasisByOrderDto, InvoiceService } from '../services/invoice.service';

export type FeeBasisMode = 'konsulent' | 'opgave';

export interface OrderOption {
  id: string;
  taskNumber: string;
  customerAndTask: string;
}

export const DAY_MONTH_YEAR_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'DD MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'DD MMMM YYYY',
  },
};

@Directive({
    selector: '[dayMonthYearFormat]',
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
        { provide: MAT_DATE_FORMATS, useValue: DAY_MONTH_YEAR_FORMAT },
    ],
    standalone: false
})
export class DayMonthYearFormat {
}

@Component({
    selector: 'app-fee-basis',
    templateUrl: './fee-basis.component.html',
    styleUrl: './fee-basis.component.scss',
    providers: [
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true, strict: false } },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FeeBasisComponent {
  loading = true;
  mode: FeeBasisMode = 'konsulent';

  consultants: Consultant[] = [];
  orderOptions: OrderOption[] = [];

  filterControl: FormControl = new FormControl([]);
  fromControl: FormControl = new FormControl();
  toControl: FormControl = new FormControl();

  consultantGroups: FeeBasisByConsultantDto[] = [];
  orderGroups: FeeBasisByOrderDto[] = [];

  constructor(
    protected consultantsService: ConsultantService,
    private orderService: OrderService,
    private invoiceService: InvoiceService,
    protected auth: AuthService
  ) {
  }

  async ngOnInit(): Promise<void> {
    [this.consultants, this.orderOptions] = await Promise.all([
      this.consultantsService.list(),
      this.orderService.feeBasisOrderOptions()
    ]);

    combineLatest({
      ids: this.filterControl.valueChanges.pipe(startWith<string[] | null>(this.filterControl.value)),
      from: this.fromControl.valueChanges.pipe(startWith(this.fromControl.value)),
      to: this.toControl.valueChanges.pipe(startWith(this.toControl.value))
    }).subscribe(({ ids, from, to }) => this.updateData(ids ?? [], from, to));

    this.loading = false;
  }

  public displayOrder = (option: OrderOption): string => {
    if (!option) return '';
    return option.customerAndTask ? `${option.taskNumber} – ${option.customerAndTask}` : option.taskNumber;
  };

  public orderValue = (option: OrderOption): string => option?.id ?? '';

  setMode(mode: FeeBasisMode) {
    if (mode === this.mode) return;
    this.mode = mode;
    this.filterControl.setValue([]);
    this.consultantGroups = [];
    this.orderGroups = [];
  }

  async updateData(ids: string[], from: Date, to: Date) {
    if (!ids || ids.length === 0 || !from || !to) {
      this.consultantGroups = [];
      this.orderGroups = [];
      return;
    }

    if (this.mode === 'konsulent') {
      this.consultantGroups = await this.invoiceService.feeBasisByConsultants(ids, from, to);
      this.orderGroups = [];
    } else {
      this.orderGroups = await this.invoiceService.feeBasisByOrders(ids, from, to);
      this.consultantGroups = [];
    }
  }

  get currentIds(): string[] {
    return (this.filterControl.value as string[]) ?? [];
  }

  get canExport(): boolean {
    return this.currentIds.length > 0 && !!this.fromControl.value && !!this.toControl.value;
  }

  async exportPdf() {
    if (!this.canExport) return;
    const file = this.mode === 'konsulent'
      ? await this.invoiceService.feeBasisByConsultantsPdf(this.currentIds, this.fromControl.value, this.toControl.value)
      : await this.invoiceService.feeBasisByOrdersPdf(this.currentIds, this.fromControl.value, this.toControl.value);
    this.downloadBlob(file.body!, this.mode === 'konsulent' ? 'honorargrundlag.pdf' : 'honorargrundlag-opgaver.pdf', 'application/pdf');
  }

  async exportExcel() {
    if (!this.canExport) return;
    const file = this.mode === 'konsulent'
      ? await this.invoiceService.feeBasisByConsultantsExcel(this.currentIds, this.fromControl.value, this.toControl.value)
      : await this.invoiceService.feeBasisByOrdersExcel(this.currentIds, this.fromControl.value, this.toControl.value);
    this.downloadBlob(file.body!, this.mode === 'konsulent' ? 'honorargrundlag.xlsx' : 'honorargrundlag-opgaver.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  private downloadBlob(body: any, filename: string, mimeType: string) {
    const blob = new Blob([body], { type: mimeType });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
